import { useEffect, useRef } from 'react';
import { Button } from '@heroui/react';
import {
  DEFAULT_VEHICLE_FIRST,
  VehicleFirst,
} from 'src/utils/interfaces/interfaces';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { UAEStatesMap } from 'src/data/States';
import { FaChevronRight, FaTruck } from 'src/icons';

export interface IMemberListProps {
  vehicle: VehicleFirst;
  onClose: Function;
  OnAction: Function;
}

export function VehiclesList({
  vehicle = DEFAULT_VEHICLE_FIRST,
  OnAction = () => {},
  onClose = () => {},
}: IMemberListProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  const { t } = useTranslation();

  const plateNumber = vehicle.plateNumber
    ? (vehicle?.plate?.emirates
        ? `${
            UAEStatesMap[vehicle?.plate.emirates as keyof typeof UAEStatesMap]
              ?.code || ''
          }-`
        : '') + vehicle.plateNumber.replace('private-', '')
    : 'N/A';

  return (
    <div
      className="grid grid-cols-12 gap-5 px-7 py-8 border-b"
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-light)',
      }}
      key={vehicle?._id}
    >
      <div className="col-span-3 grid grid-cols-7 gap-5">
        <div
          className="inline col-span-2 self-center h-14 w-14 flex items-center justify-center rounded-full shadow-lg"
          style={{
            backgroundColor: 'var(--color-primary-light)',
            color: 'var(--color-primary)',
            border: '1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)',
          }}
        >
          <FaTruck className="text-xl" />
        </div>
        <div className="col-span-5">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {t('vehicle.plate_no')}
          </p>
          <div>
            <p className="text-sm capitalize" style={{ color: 'var(--text-main)' }}>
              {plateNumber}
            </p>
          </div>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('vehicle.make')}
        </p>
        <div>
          <p className="text-sm capitalize" style={{ color: 'var(--text-main)' }}>
            {vehicle?.vehicleMake || 'Unknown'}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('vehicle.model')}
        </p>
        <div>
          <p className="text-sm" style={{ color: 'var(--text-main)' }}>
            {vehicle?.model || 'N/A'}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('vehicle.chassis')}
        </p>
        <div>
          <p className="text-sm" style={{ color: 'var(--text-main)' }}>
            {vehicle?.chassis || 'N/A'}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('vehicle.mulkiya_expiry')}
        </p>
        <div>
          <p className="text-sm capitalize" style={{ color: 'var(--text-main)' }}>
            {vehicle?.mulkiya
              ? moment(vehicle.mulkiya).format('DD/MM/YYYY')
              : 'N/A'}
          </p>
        </div>
      </div>

      <div className="col-span-1 flex items-center justify-end">
        <Button
          variant="light"
          size="sm"
          onPress={() => OnAction(vehicle)}
          onMouseDown={(e) => e.preventDefault()}
          isIconOnly
          className="rounded-full shadow-lg border transition-colors duration-200 outline-none focus:outline-none focus:ring-0 data-[focus-visible=true]:outline-none data-[focus-visible=true]:ring-0"
          style={{
            backgroundColor: 'var(--bg-surface-2)',
            borderColor: 'var(--border-light)',
            color: 'var(--color-primary)',
            outline: 'none',
            boxShadow: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
          startContent={<FaChevronRight />}
        />
      </div>
    </div>
  );
}
